/**
 * Markdown/documentation file extractor.
 *
 * Extracts document structure and cross-references to code from .md, .txt, .rst files.
 * Does NOT use tree-sitter — parses markdown directly via regex (heading extraction).
 *
 * Node types produced:
 *   - "document" — one per file (top-level)
 *   - "section" — one per heading (## Heading)
 *
 * Reference detection:
 *   - Backtick spans: `function_name` → references to code symbols
 *   - File paths: src/config/loader.py → references to files
 *   - Code blocks with imports → parsed for import references
 */
import type { LanguageExtractor, SyntaxTree, FileExtraction, SymbolNode, ImportDeclaration, SymbolReference, FileNodeDeclaration } from "../types.js";
import { posixBasename, toPosix } from "../../shared/paths.js";

export class MarkdownExtractor implements LanguageExtractor {
  readonly languageId = "markdown";
  readonly extensions = [".md", ".txt", ".rst"];
  // No tree-sitter — uses regex-based parsing
  readonly wasmFile = undefined;

  extract(_tree: SyntaxTree | null, sourceCode: string, filePath: string): FileExtraction {
    const lines = sourceCode.split(/\r?\n/);
    const symbols: SymbolNode[] = [];
    const imports: ImportDeclaration[] = [];
    const references: SymbolReference[] = [];

    const docName = posixBasename(filePath);

    // Declare the file-level node via fileNode (NOT in symbols[])
    const fileNode: FileNodeDeclaration = {
      kind: "document",
      label: docName,
      docstring: this.extractFirstParagraph(lines),
    };

    // Extract sections from headings (these are the internal contents)
    const sections = this.extractSections(lines, filePath, docName);
    symbols.push(...sections);

    // Extract code references (backtick spans, file paths)
    const refs = this.extractCodeReferences(lines, filePath, docName, sections);
    references.push(...refs);

    return { filePath, language: "markdown", fileNode, symbols, imports, references };
  }

  resolveImportPath(_importModule: string, _currentFilePath: string): string[] {
    // Docs don't have imports in the traditional sense
    return [];
  }

  // ─── Private Methods ─────────────────────────────────────────────────────

  private extractFirstParagraph(lines: string[]): string | undefined {
    const nonEmpty: string[] = [];
    let started = false;

    for (const line of lines) {
      const trimmed = line.trim();
      // Skip YAML frontmatter
      if (!started && trimmed === "---") {
        started = false;
        continue;
      }
      // Skip headings at the top
      if (!started && trimmed.startsWith("#")) {
        started = true;
        continue;
      }
      if (!started && trimmed === "") continue;
      started = true;

      if (trimmed === "") {
        if (nonEmpty.length > 0) break;
        continue;
      }
      if (trimmed.startsWith("#")) break;
      nonEmpty.push(trimmed);
    }

    if (nonEmpty.length === 0) return undefined;
    const paragraph = nonEmpty.join(" ");
    return paragraph.length > 200 ? paragraph.slice(0, 200) + "..." : paragraph;
  }

  private extractSections(lines: string[], filePath: string, docName: string): SymbolNode[] {
    const sections: SymbolNode[] = [];
    const moduleName = this.filePathToModuleName(filePath);
    const sectionCounts = new Map<string, number>();
    let currentStart = -1;
    let currentName = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

      // Also handle RST-style underline headings
      const rstMatch = i > 0 && /^[=\-~^]+$/.test(line.trim()) && line.trim().length >= 3
        ? lines[i - 1]?.trim()
        : null;

      const heading = headingMatch?.[2]?.trim() ?? rstMatch ?? null;
      const level = headingMatch ? headingMatch[1]!.length : (rstMatch ? 2 : 0);

      if (heading && level > 0) {
        // Close previous section
        if (currentStart > 0 && currentName) {
          sections[sections.length - 1]!.endLine = i;
        }

        // Display name preserves spaces; sanitized form used only for qualifiedName (node ID)
        const displayName = this.cleanHeading(heading);
        const sanitizedName = this.sanitizeSectionName(heading);
        const count = (sectionCounts.get(sanitizedName) ?? 0) + 1;
        sectionCounts.set(sanitizedName, count);
        const uniqueSanitized = count === 1 ? sanitizedName : `${sanitizedName}_${count}`;
        const qualified = `${moduleName}.${uniqueSanitized}`;

        sections.push({
          name: displayName,
          qualifiedName: qualified,
          kind: "section",
          decorators: [`h${level}`],
          docstring: heading,
          startLine: i + 1,
          endLine: lines.length, // Will be updated when next section found
          parent: docName,
        });

        currentStart = i + 1;
        currentName = displayName;
      }
    }

    return sections;
  }

  private extractCodeReferences(
    lines: string[],
    filePath: string,
    _docName: string,
    sections: SymbolNode[],
  ): SymbolReference[] {
    const references: SymbolReference[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      const lineNum = i + 1;

      // Find the current section for context (returns qualifiedName = node ID)
      const currentSection = this.findSectionAtLine(lineNum, sections, filePath);

      // Extract backtick code spans: `symbol_name`
      const backtickRegex = /`([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)`/g;
      let match;
      while ((match = backtickRegex.exec(line)) !== null) {
        const name = match[1]!;
        // Skip very short names and common markdown artifacts
        if (name.length < 2 || /^(true|false|null|none|if|else|for|while|return|import|from|class|def)$/i.test(name)) continue;
        const key = `${currentSection}:${name}`;
        if (!seen.has(key)) {
          seen.add(key);
          references.push({
            name,
            fromSymbol: currentSection,
            kind: "references", // Code reference from doc
            line: lineNum,
          });
        }
      }

      // Extract file path references: src/path/file.py or ./relative/file.ts
      const pathRegex = /(?:^|[\s("`'])([a-zA-Z0-9_./-]+\.[a-z]{1,4})(?:[\s)"`']|$)/g;
      while ((match = pathRegex.exec(line)) !== null) {
        const path = match[1]!;
        // Must look like a source file path (has directory separator and code extension)
        if (!path.includes("/")) continue;
        if (!/\.(py|ts|tsx|js|jsx|java|go|rs|rb|c|cpp|h|hpp)$/.test(path)) continue;
        const key = `${currentSection}:file:${path}`;
        if (!seen.has(key)) {
          seen.add(key);
          references.push({
            name: path,
            fromSymbol: currentSection,
            kind: "references", // File reference from doc
            line: lineNum,
          });
        }
      }
    }

    return references;
  }

  private findSectionAtLine(lineNum: number, sections: SymbolNode[], filePath: string): string {
    for (let i = sections.length - 1; i >= 0; i--) {
      if (lineNum >= sections[i]!.startLine) {
        return sections[i]!.qualifiedName;
      }
    }
    return filePath;
  }

  private cleanHeading(heading: string): string {
    // Remove markdown formatting only, preserve spaces for readable labels
    return heading
      .replace(/[*_~`\[\]()]/g, "")
      .replace(/[^a-zA-Z0-9_ -]/g, "")
      .trim()
      .slice(0, 80);
  }

  private sanitizeSectionName(heading: string): string {
    // Remove markdown formatting, keep meaningful text
    return heading
      .replace(/[*_~`\[\]()]/g, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .slice(0, 80);
  }

  private filePathToModuleName(filePath: string): string {
    const normalized = toPosix(filePath);
    return normalized.replace(/\.[^.]+$/, "").replace(/\//g, ".");
  }
}
